import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AWVConfiguration } from 'src/app/awv/awv-patient-responses/awv-patient-responses.model';
import { AwvSendFormRequest, AwvSendFormResponse } from 'src/app/awv/models/awv.models';
import { Claim, ClaimResponse } from 'src/app/models/billing/claims-model';
import { Client } from 'src/app/models/client.model';
import { FirestoreService } from 'src/app/services/firestore.service';
import { environment } from 'src/environments/environment';
import { AnswerForm, AwvForm, Question, Section } from '../models/awv-default.model';

@Injectable({
  providedIn: 'root',
})
export class AwvDefaultConfigurationService {
  constructor(
    private firestoreService: FirestoreService,
    private http: HttpClient
  ) {}

  getFormById(id: string): Observable<AnswerForm> {
    return this.firestoreService.doc$<AnswerForm>(`awv_form_responses/${id}`);
  }

  getAwvDefaultForm(): Observable<AwvForm> {
    return this.firestoreService.doc$<AwvForm>('welby_configuration/awv_default_form');
  }

  deleteQuestion(question: Question, section: Section): Promise<void> {
    const updateQuestions = section.questions.filter((q) => q.questionText !== question.questionText);

    return this.firestoreService
      .doc(`welby_configuration/awv_default_form`)
      .update({ [section.category]: { ...section, questions: updateQuestions } });
  }

  async updateSortIndex(sections: Section[]): Promise<void> {
    try {
      const form = {};
      sections.forEach((section, index) => {
        section.sortIndex = index + 1;
        section.questions.forEach((question, qIndex) => {
          question.sortIndex = qIndex + 1;
        });
        form[section.category] = section;
      });
      await this.firestoreService.doc(`welby_configuration/awv_default_form`).update(form);
    } catch (error) {
      console.error(error);
    }
  }

  async saveAnswersForm(answerForm: AnswerForm): Promise<string> {
    if (!answerForm.id) {
      answerForm.id = this.firestoreService.createId();
      await this.firestoreService.doc(`awv_form_responses/${answerForm.id}`).set({ ...answerForm, completed: false });
    } else {
      await this.firestoreService
        .doc(`awv_form_responses/${answerForm.id}`)
        .update({ ...answerForm, completed: false });
    }
    return answerForm.id;
  }

  async completeAWV(formId: string, patientId: string): Promise<void> {
    const currentDate = this.firestoreService.timestamp;
    await this.firestoreService.doc(`awv_form_responses/${formId}`).update({
      completed: true,
      updatedAt: currentDate,
      completedAt: currentDate,
    });
    return this.firestoreService.doc(`users/${patientId}`).update({
      last_awv_form: { id: formId, date: currentDate },
    });
  }

  async batchUpdateClaimData(
    formId: string,
    claim: Claim,
    response: ClaimResponse,
    patientUpdate?: { patientId: string; initialAwv: Record<string, unknown> }
  ): Promise<void> {
    const batch = this.firestoreService.batch();

    const formRef = this.firestoreService.doc(`awv_form_responses/${formId}`).ref;
    batch.update(formRef, {
      claim: {
        dos: claim.dos,
        createdAt: claim.createdAt,
        claimResponse: response,
      },
    });

    if (patientUpdate && response.claimids?.length) {
      const patientRef = this.firestoreService.doc(`users/${patientUpdate.patientId}`).ref;
      batch.update(patientRef, {
        initial_awv: {
          ...patientUpdate.initialAwv,
          claim_id: response.claimids[0],
        },
      });
    }

    await batch.commit();
  }

  getFormResponsesByPatientId(patientId: string): Observable<any[]> {
    return this.firestoreService.colWithIds$('awv_form_responses', (ref) =>
      ref.where('patient_id', '==', patientId).orderBy('createdAt', 'desc')
    );
  }
  async updateSection(section: Section): Promise<void> {
    await this.firestoreService.doc(`welby_configuration/awv_default_form`).update({ [section.category]: section });
  }

  createPdf(form: any): Observable<any> {
    const url = `${environment.welbyEndpoint}/api/v1/core/awv-form/generate-awv-pdf/${form.id}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  isEnableAwvForm(client_id: string): Observable<boolean> {
    return this.firestoreService
      .doc<{ awv_form_isEnable: boolean }>(`welby_configuration/awv_form_configuration`)
      .valueChanges()
      .pipe(
        switchMap((doc) => {
          if (!doc.awv_form_isEnable) {
            return of(false);
          }
          return this.firestoreService.doc<Client>(`clients/${client_id}`).valueChanges();
        }),
        map((client) => {
          if (typeof client === 'boolean') {
            return client;
          }
          return client.awv_form_isEnable;
        })
      );
  }

  getInterpretationScore(): Observable<AWVConfiguration> {
    return this.firestoreService.doc<AWVConfiguration>('welby_configuration/awv_form_configuration').valueChanges();
  }

  sendFormToPatient(data: AwvSendFormRequest): Observable<AwvSendFormResponse> {
    return this.http.post<AwvSendFormResponse>(`${environment.welbyEndpoint}/api/v1/core/awv-form/send-link`, data);
  }

  getMarkusRecommendation(id: string): Observable<{ markus_summary: string }> {
    return this.http.post<{ markus_summary: string }>(
      `${environment.welbyEndpoint}/api/v1/markus-actions/generate-awv-recommendations-by-id`,
      {
        awv_form_response_id: id,
        generateGoogleDoc: true,
      }
    );
  }
}
